// Read state helpers: what counts as unread or a mention, the order channels appear in the sidebar (for Alt+↑/↓
// and the quick switcher), the tab title badge, and knowing when the tab is visible. The positions themselves live
// in the room (synced across devices) and in Room's snapshot (realtime.ts).
import { useEffect, useState } from "react";
import type { Category, Channel, Message } from "@bloom/shared";
import type { Snapshot } from "./realtime.ts";

/** Reads are sent to the room after this much quiet, so scrolling a busy channel doesn't send a frame per message. */
export const READ_DEBOUNCE_MS = 400;

/**
 * Does this message bump my mention badge? Someone else's message that @mentions me or @everyone, or any message
 * from someone else in a DM. Must agree with the room's count (worker/src/room.ts, unreadMentionCounts).
 */
export function countsAsMention(m: Pick<Message, "authorId" | "mentions">, me: string | null, channel: Pick<Channel, "kind"> | undefined): boolean {
  if (!me || m.authorId === me) return false;
  if (channel?.kind === "dm") return true;
  return !!m.mentions && (m.mentions.includes(me) || m.mentions.includes("everyone"));
}

/** Has messages I haven't read. Nothing is unread until the room has told us where we are. */
export function isUnread(c: Channel, snap: Pick<Snapshot, "read" | "me">): boolean {
  return !!snap.me && c.lastSeq > (snap.read[c.id] ?? 0);
}

/**
 * Channels in the order the sidebar shows them: the DMs listed at the top (`dms`, already in the sidebar's order),
 * then uncategorised channels, then each category in order. Alt+↑/↓ and the quick switcher follow this.
 */
export function sidebarOrder(channels: Channel[], categories: Category[], dms: Channel[]): Channel[] {
  const text = channels.filter((c) => c.kind !== "dm");
  const inCat = (id: string | null) => text.filter((c) => c.categoryId === id).sort((a, b) => a.position - b.position);
  const cats = [...categories].sort((a, b) => a.position - b.position);
  return [...dms, ...inCat(null), ...cats.flatMap((cat) => inCat(cat.id))];
}

/**
 * Where Alt+↑/↓ goes from the open channel (`currentId`) in the sidebar's `order`, wrapping at either end like
 * Discord's. With `unreadOnly` (Alt+Shift) it skips channels with nothing unread and muted ones, unread or not.
 * Undefined when nothing qualifies.
 */
export function stepTarget<C extends { id: string }>(order: readonly C[], currentId: string | undefined, dir: -1 | 1, unreadOnly: boolean, unread: (c: C) => boolean, muted: (id: string) => boolean): C | undefined {
  const at = order.findIndex((c) => c.id === currentId);
  const n = order.length;
  for (let i = 1; i < n; i++) {
    const c = order[(((at + dir * i) % n) + n) % n]!;
    if (!unreadOnly || (c.id !== currentId && unread(c) && !muted(c.id))) return c;
  }
  return undefined;
}

/** "#general", or for a DM the other people's names ("Maya, Sam"). */
export function channelLabel(c: Channel, me: string | null, names: Map<string, string>): string {
  if (c.kind !== "dm") return c.name;
  const others = (c.members ?? []).filter((id) => id !== me).map((id) => names.get(id) ?? "Someone");
  return others.join(", ") || c.name || "Direct message";
}

/** True while the tab is in front of the person (not hidden or minimised). */
export function useVisible(): boolean {
  const [visible, setVisible] = useState(() => document.visibilityState === "visible");
  useEffect(() => {
    const on = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", on);
    return () => document.removeEventListener("visibilitychange", on);
  }, []);
  return visible;
}

/**
 * The tab title, like Discord's: "(3) Bloom" with unread mentions and DM messages (in all my servers), "• Bloom" with
 * only unread messages in conversations I haven't muted (in any of my servers), plain "Bloom" otherwise. The dot is
 * the small "•" (a title can't be styled, so the glyph is its size).
 */
export function titleFor(mentions: number, unread: boolean): string {
  const base = "Bloom";
  if (mentions > 0) return `(${mentions > 99 ? "99+" : mentions}) ${base}`;
  return unread ? `• ${base}` : base;
}

/** Keeps the tab title in step (titleFor), and puts it back to plain "Bloom" when the page goes. */
export function useTitleBadge(mentions: number, unread = false) {
  useEffect(() => {
    document.title = titleFor(mentions, unread);
  }, [mentions, unread]);
  useEffect(
    () => () => {
      document.title = "Bloom";
    },
    [],
  );
}
